/**
 * Единый разбор ответов сервера в понятное сообщение.
 *
 * Сервер отвечает по-разному: { detail }, { message }, { error },
 * { поле: ["текст"] } от сериализатора; плюс бывают сетевые сбои,
 * таймауты и HTML-страницы от nginx (413/502).
 *
 * Возвращаем одинаковую форму: { ok:false, status, code, message, fields }
 */

const NETWORK_TEXT = 'Нет связи с сервером. Проверьте интернет и попробуйте ещё раз.';
const TIMEOUT_TEXT = 'Сервер долго не отвечает. Попробуйте ещё раз через минуту.';
const SERVER_TEXT  = 'Техническая ошибка на сервере. Попробуйте позже или напишите нам.';

const pickText = (data) => {
    if (!data) return '';
    if (typeof data === 'string') {
        // HTML-страница от nginx — пользователю показывать нечего
        return data.trim().startsWith('<') ? '' : data.trim();
    }
    if (typeof data.detail === 'string') return data.detail;
    if (typeof data.message === 'string') return data.message;
    if (typeof data.error === 'string') return data.error;

    // detail может быть объектом ошибок сериализатора
    const nested = (typeof data.detail === 'object' && data.detail) ? data.detail : data;
    const firstKey = Object.keys(nested).find(
        k => !['status', 'code', 'errors', 'retry_after'].includes(k)
    );
    if (firstKey) {
        const value = nested[firstKey];
        if (Array.isArray(value) && value.length) return String(value[0]);
        if (typeof value === 'string') return value;
    }
    return '';
};

const pickFields = (data) => {
    if (!data || typeof data !== 'object') return {};
    const source = (typeof data.detail === 'object' && data.detail) ? data.detail : data;
    const out = {};
    Object.entries(source).forEach(([key, value]) => {
        if (['detail', 'message', 'error', 'status', 'code', 'errors', 'retry_after'].includes(key)) return;
        if (Array.isArray(value) && value.length) out[key] = String(value[0]);
        else if (typeof value === 'string') out[key] = value;
    });
    return out;
};

export const normalizeApiError = (error, fallback = 'Не удалось выполнить запрос') => {
    if (error && error.ok === false && error.message) return error;   // уже разобрано

    const status = error?.response?.status || error?.status || 0;
    const data = error?.response?.data ?? error?.data ?? null;

    let message = pickText(data);
    let code = (data && typeof data === 'object' && data.code) || '';

    if (!message) {
        if (error?.code === 'ECONNABORTED') { message = TIMEOUT_TEXT; code = code || 'timeout'; }
        else if (error?.code === 'ERR_NETWORK' || status === 0) { message = NETWORK_TEXT; code = code || 'network'; }
        else if (status === 413) { message = 'Файл слишком большой.'; code = code || 'too_large'; }
        else if (status === 401) { message = 'Сессия истекла — войдите заново.'; code = code || 'unauthorized'; }
        else if (status === 403) { message = 'Недостаточно прав для этого действия.'; code = code || 'forbidden'; }
        else if (status === 404) { message = 'Не найдено. Проверьте введённые данные.'; code = code || 'not_found'; }
        else if (status === 429) { message = 'Слишком часто. Подождите немного.'; code = code || 'rate_limit'; }
        else if (status >= 500) { message = SERVER_TEXT; code = code || 'server_error'; }
        else message = fallback;
    }

    return {
        ok: false,
        status,
        code,
        message,
        fields: pickFields(data),
        retry_after: (data && data.retry_after) || null,
    };
};

/** true, если объект — нормализованная ошибка */
export const isApiError = (value) => !!value && value.ok === false;

/**
 * Текст ошибки из любого ответа — короткий помощник для мест,
 * где нужен только текст и не нужны поля.
 */
export const errorText = (value, fallback = 'Не удалось выполнить запрос') => {
    if (!value) return fallback;
    if (value.ok === false) return value.message || fallback;
    if (typeof value.detail === 'string') return value.detail;
    if (typeof value.message === 'string') return value.message;
    return fallback;
};